import React, { useEffect, useRef, useState } from 'react';
import { createChart, IChartApi, ISeriesApi, CandlestickData, Time, CandlestickSeries } from 'lightweight-charts';
import './BacktestChart.css';

interface Trade {
  time: number;
  type: 'buy' | 'sell';
  price: number;
  quantity: number;
  pnl?: number;
  reason?: string;
}

interface GridLevel {
  price: number;
  active: boolean;
}

interface BacktestChartProps {
  data: CandlestickData[];
  trades: Trade[];
  gridLevels?: GridLevel[];
  onTimeChange?: (time: number) => void;
}

const BacktestChart: React.FC<BacktestChartProps> = ({
  data,
  trades,
  gridLevels = [],
  onTimeChange
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(-1); // -1 = show all data
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const playbackRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize chart
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { color: '#0a0a0a' },
        textColor: '#e5e7eb',
      },
      grid: {
        vertLines: { color: '#1a1a1a' },
        horzLines: { color: '#1a1a1a' },
      },
      width: chartContainerRef.current.clientWidth,
      height: 500,
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
      },
      rightPriceScale: {
        borderColor: '#2a2a2a',
      },
      crosshair: {
        mode: 1,
      },
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#22c55e',
      downColor: '#ef4444',
      borderVisible: false,
      wickUpColor: '#22c55e',
      wickDownColor: '#ef4444',
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chart) {
        chart.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, []);

  // Update chart data based on current playback position
  useEffect(() => {
    if (!candleSeriesRef.current || !data.length) return;

    const showAll = currentIndex < 0;
    const maxIndex = showAll ? data.length - 1 : currentIndex;
    const visibleData = data.slice(0, maxIndex + 1);
    candleSeriesRef.current.setData(visibleData);

    // Add trade markers
    const markers = trades
      .filter(trade => {
        if (showAll) return true;
        const tradeIndex = data.findIndex(d => d.time === trade.time);
        return tradeIndex >= 0 && tradeIndex <= maxIndex;
      })
      .map(trade => ({
        time: trade.time as Time,
        position: trade.type === 'buy' ? 'belowBar' : 'aboveBar',
        color: trade.type === 'buy' ? '#22c55e' : '#ef4444',
        shape: trade.type === 'buy' ? 'arrowUp' : 'arrowDown',
        text: trade.type === 'buy'
          ? `BUY ${trade.price.toFixed(2)}`
          : `SELL ${trade.price.toFixed(2)} (${trade.pnl ? (trade.pnl > 0 ? '+' : '') + trade.pnl.toFixed(0) : ''})`,
      }));

    // lightweight-charts requires markers sorted by time
    markers.sort((a, b) => (a.time as number) - (b.time as number));
    (candleSeriesRef.current as any).setMarkers(markers as any);

    if (onTimeChange && data[maxIndex]) {
      onTimeChange(data[maxIndex].time as number);
    }
  }, [currentIndex, data, trades, onTimeChange]);

  // Grid levels as price lines
  useEffect(() => {
    if (!candleSeriesRef.current || !gridLevels.length) return;

    // Remove old price lines (this is a simplified approach)
    // In production, you'd want to track line references

    gridLevels.forEach(level => {
      if (level.active && candleSeriesRef.current) {
        candleSeriesRef.current.createPriceLine({
          price: level.price,
          color: '#3b82f6',
          lineWidth: 1,
          lineStyle: 2, // Dashed
          axisLabelVisible: true,
          title: '',
        });
      }
    });
  }, [gridLevels]);

  // Playback control
  useEffect(() => {
    if (isPlaying && currentIndex < data.length - 1) {
      const interval = 1000 / playbackSpeed;
      playbackRef.current = setInterval(() => {
        setCurrentIndex(prev => {
          const idx = prev < 0 ? 0 : prev; // start from beginning if in "show all" mode
          if (idx >= data.length - 1) {
            setIsPlaying(false);
            return idx;
          }
          return idx + 1;
        });
      }, interval);
    } else if (!isPlaying && playbackRef.current) {
      clearInterval(playbackRef.current);
      playbackRef.current = null;
    }

    return () => {
      if (playbackRef.current) {
        clearInterval(playbackRef.current);
      }
    };
  }, [isPlaying, playbackSpeed, currentIndex, data.length]);

  const handlePlayPause = () => {
    if (!isPlaying && currentIndex < 0) {
      setCurrentIndex(0); // switch from "show all" to playback at start
    }
    setIsPlaying(!isPlaying);
  };

  const handleStop = () => {
    setIsPlaying(false);
    setCurrentIndex(-1); // back to "show all"
  };

  const handleSpeedChange = (speed: number) => {
    setPlaybackSpeed(speed);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newIndex = parseInt(e.target.value);
    setCurrentIndex(newIndex);
  };

  const formatDate = (time: number) => {
    return new Date(time * 1000).toLocaleString('ru-RU');
  };

  return (
    <div className="backtest-chart-container">
      <div className="chart-wrapper" ref={chartContainerRef} />

      <div className="playback-controls">
        <div className="control-buttons">
          <button
            className="control-btn"
            onClick={handleStop}
            title="Сбросить"
          >
            ⏹
          </button>
          <button
            className="control-btn primary"
            onClick={handlePlayPause}
            title={isPlaying ? 'Пауза' : 'Воспроизвести'}
          >
            {isPlaying ? '⏸' : '▶'}
          </button>
        </div>

        <div className="timeline">
          <input
            type="range"
            min="0"
            max={data.length - 1}
            value={currentIndex < 0 ? data.length - 1 : currentIndex}
            onChange={handleSeek}
            className="timeline-slider"
          />
          <div className="timeline-info">
            <span className="timeline-text">
              {currentIndex < 0
                ? `${data.length > 0 ? formatDate(data[0].time as number) : ''} — ${data.length > 0 ? formatDate(data[data.length - 1].time as number) : ''}`
                : data[currentIndex] ? formatDate(data[currentIndex].time as number) : ''
              }
            </span>
            <span className="timeline-text">
              {currentIndex < 0 ? `Все: ${data.length}` : `${currentIndex + 1} / ${data.length}`}
            </span>
          </div>
        </div>

        <div className="speed-controls">
          <span className="speed-label">Скорость:</span>
          {[1, 2, 5, 10].map(speed => (
            <button
              key={speed}
              className={`speed-btn ${playbackSpeed === speed ? 'active' : ''}`}
              onClick={() => handleSpeedChange(speed)}
            >
              {speed}x
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BacktestChart;
